import { toast } from '@parsleyai/ui'
import { createContext, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { ArmedUndo, UndoableAction, UndoContextValue } from './types'

export const UndoContext = createContext<UndoContextValue | undefined>(undefined)

// How long the undo toast stays actionable. The armed slot outlives it — the toast is
// the transient surface, the ⌘Z shortcut and header affordance (US-4) are the ones that
// persist until the next action or a reload.
const UNDO_TOAST_MS = 6000

// Don't hijack the browser's native text-undo: ignore ⌘Z/Ctrl+Z while the user is in a
// text-entry field.
function isTextEntry(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable
}

/**
 * The app-wide undo service. Command-pattern: callers hand `run()` an action that commits and reverses
 * itself; the service commits EAGERLY so the write survives navigation, then arms one undo reachable from
 * the toast, ⌘Z, and the header. Single-level, in-memory, session-scoped — no stack, no redo.
 */
export function UndoProvider({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const [armed, setArmed] = useState<ArmedUndo | null>(null)

  // The keyboard handler is bound once; a ref keeps it reading the latest armed slot
  // without rebinding on every arm/disarm.
  const armedRef = useRef<ArmedUndo | null>(null)
  armedRef.current = armed

  const run = useCallback(
    async <M,>(action: UndoableAction<M>) => {
      let memento: M
      try {
        memento = await action.commit()
      } catch {
        // Commit failed — the action didn't happen, so nothing is armed.
        toast.error(action.errorToast ?? t('undo.error'))
        return
      }

      // `done` makes the undo idempotent so the toast button and ⌘Z can't both fire it.
      let done = false
      const slot: ArmedUndo = {
        label: action.undoLabel ?? action.toast,
        undo: async () => {
          if (done) return
          done = true
          // Clear the slot only if it's still the armed one (a newer action may have
          // replaced it while this toast lingered).
          setArmed((current) => (current === slot ? null : current))
          try {
            await action.undo(memento)
            toast.success(action.undoneToast ?? t('undo.done'))
          } catch {
            // Undo failed — the action is still committed AND still undoable. Re-arm the slot
            // (unless a newer action already claimed it) so every surface — toast, ⌘Z, and the
            // header affordance — can retry, not just the toast button while it lingers.
            done = false
            setArmed((current) => (current === null ? slot : current))
            toast.error(action.errorToast ?? t('undo.error'))
          }
        }
      }

      setArmed(slot)
      toast(action.toast, {
        action: { label: t('undo.action'), onClick: () => void slot.undo() },
        duration: UNDO_TOAST_MS
      })
    },
    [t]
  )

  // ⌘Z (macOS) / Ctrl+Z (Windows/Linux) undoes the armed action — the desktop safety net
  // that outlives the toast. Shift excluded (that's redo, which we don't have).
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || event.shiftKey || event.altKey) return
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'z') return
      if (isTextEntry(event.target)) return
      const slot = armedRef.current
      if (!slot) return
      event.preventDefault()
      void slot.undo()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const value = useMemo<UndoContextValue>(() => ({ run, armed }), [run, armed])

  return <UndoContext.Provider value={value}>{children}</UndoContext.Provider>
}
